import { useVisitorData } from '@fingerprintjs/fingerprintjs-pro-react'
import { Button, Checkbox, cn, Form, Input, InputOtp } from '@heroui/react'
import { Turnstile } from '@marsidev/react-turnstile'
import AccountSelect from 'components/Auth/Login/AccountSelect'
import AuthButton from 'components/Hero/AuthButton'
import PhoneInput from 'components/Hero/PhoneInput'
import { TURNSTILE_SITE_KEY } from 'config/turnstile'
import { motion } from 'framer-motion'
import { useAuth } from 'hooks/useAuth'
import { Key } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useCookie } from 'react-use'
import { useStep } from 'usehooks-ts'
import { $fetch } from 'utils/fetch'
import logo from '../assets/brand/bland-b.svg'
import { Promotion } from '../components/Auth/SignUp/Promotion'
import WebDemo from './Onboarding/WebDemo'
import RegionSelector from './RegionSelector'
import 'react-phone-input-2/lib/style.css'

export default function Auth() {
  const navigate = useNavigate()
  const { handleAuthResponse, user } = useAuth()
  const { register, handleSubmit, control } = useForm()
  const [searchParams, setSearchParams] = useSearchParams()
  const [browserSessionToken, updateBrowserSessionToken, deleteBrowserSessionToken] = useCookie('__Secure-bland-auth.session_token')
  const { getData: generateFingerprint } = useVisitorData(
    { ignoreCache: true },
    { immediate: false },
  )

  const [turnstileToken, setTurnstileToken] = useState('')
  const [turnstileError, setTurnstileError] = useState(true)
  const [isLoading, setLoading] = useState(false)
  const [currentStep, helpers] = useStep(4)
  const [isSSOLogin, setIsSSOLogin] = useState(false)

  const {
    goToNextStep,
    setStep,
  } = helpers

  const isLogin = window.location.pathname === '/login'
  const isSSOCallback = searchParams.get('callback') === 'true'

  const handleTurnstileSuccess = (token) => {
    setTurnstileError(false)
    setTurnstileToken(token)
  }

  const handleTurnstileError = () => {
    setTurnstileError(true)
  }

  const sendOtp = async ({ phone }) => {
    try {
      await $fetch(isSSOCallback ? '/sso/sendOtp' : '/signup/sendOtp', {
        method: 'POST',
        body: {
          phoneNumber: phone,
          token: turnstileToken,
          context: isLogin ? 'login' : 'signup',
        },
      })
      toast.success('Verification code sent successfully!')
      goToNextStep()
    }
    catch (error) {
      console.error(error)
      const errorMessage = error.data?.errors?.[0]?.message
      if (errorMessage === 'Invalid token') {
        toast.error('Security check failed. Please wait a few minutes then refresh the page before trying again.')
        return
      }
      toast.error(errorMessage ?? 'An unknown error occurred')
    }
  }

  const verifyLoginOtp = async ({ phone, sms_code, deviceId }) => {
    try {
      const { data } = await $fetch(isSSOCallback ? '/sso/verifyOtp' : '/login/verifyOtp', {
        method: 'POST',
        body: {
          phoneNumber: phone,
          code: sms_code,
          deviceId,
        },
      })
      if (isSSOCallback) {
        navigate('/dashboard')
      }
      else {
        await handleAuthResponse({ jwt: data.jwt })
        toast.success('Logged in successfully!')
        setStep(4)
      }
    }
    catch (error) {
      console.error(error)
      const errorMessage = error.data?.errors?.[0]?.message
      toast.error(errorMessage ?? 'An unknown error occurred')
    }
  }

  const verifySignupOtp = async ({ phone, sms_code, first_name, last_name, email, email_code, marketing_consent, deviceId }) => {
    try {
      const { data } = await $fetch('/signup/verifyOtp', {
        method: 'POST',
        body: {
          phoneNumber: phone,
          code: sms_code,
          email_code,
          deviceId,
          signupData: {
            first_name,
            last_name,
            email,
            marketing_consent: marketing_consent ? ['sms', 'email'] : undefined,
          },
        },
      })
      if (data.email_verification_required) {
        goToNextStep()
      }
      else {
        await handleAuthResponse({ jwt: data.jwt, org_id: data.initial_org_id })

        // Track signup with Default SDK (fire and forget to avoid modal)
        if (window.DefaultSDK) {
          // Don't await this - just fire the tracking and move on
          window.DefaultSDK.submit({
            form_id: 322740,
            team_id: 157,
            responses: {
              first_name,
              last_name,
              email,
              phone,
              marketing_consent: marketing_consent || false,
            },
            questions: [
              {
                id: 'first_name',
                name: 'First Name',
                type: 'text',
                options: [],
              },
              {
                id: 'last_name',
                name: 'Last Name',
                type: 'text',
                options: [],
              },
              {
                id: 'email',
                name: 'Email',
                type: 'email',
                options: [],
              },
              {
                id: 'phone',
                name: 'Phone Number',
                type: 'text',
                options: [],
              },
              {
                id: 'marketing_consent',
                name: 'Marketing Consent',
                type: 'boolean',
                options: [],
              },
            ],
          }).catch((error) => {
            console.error('Default SDK tracking error:', error)
          })
        }

        setStep(4)
      }
    }
    catch (error) {
      console.error(error)
      const errorMessage = error.data?.errors?.[0]?.message
      toast.error(errorMessage ?? 'An unknown error occurred')
    }
  }

  const handleSSOLogin = async (formData) => {
    try {
      setLoading(true)

      const { url } = await $fetch('/authorization/sign-in/sso', {
        method: 'POST',
        body: {
          email: formData.email,
          callbackURL: `${window.location.origin}/login?callback=true`,
          scopes: ['openid', 'email', 'profile'],
        },
      })

      window.location.href = url
    }
    catch (error) {
      console.error(error)
      toast.error('Failed to initiate SSO login')
    }
    finally {
      setLoading(false)
    }
  }

  const onSubmit = async (formData) => {
    setLoading(true)
    try {
      if (currentStep === 1) {
        if (turnstileError) {
          toast.error('Please complete the security check, or refresh the page and try again.')
          return
        }
        if (isSSOLogin) {
          await handleSSOLogin(formData)
          return
        }
        await sendOtp(formData)
      }
      if (currentStep === 2 || currentStep === 3) {
        if (import.meta.env.VITE_VERCEL_ENV === 'production') {
          try {
            const fingerprintResult = await generateFingerprint()
            formData.deviceId = fingerprintResult?.requestId
          }
          catch (error) {
            console.error(error)
          }
        }

        if (isLogin) {
          await verifyLoginOtp(formData)
        }
        else {
          await verifySignupOtp(formData)
        }
      }
    }
    finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!isLogin) {
      const trackingPixel = document.createElement('img')
      trackingPixel.src = 'https://jelly.mdhv.io/v1/star.gif?pid=1GVTNGo1I6zeX2vulU5ZEL2tnVrO&src=mh&evt=hi'
      trackingPixel.style.display = 'none'
      trackingPixel.width = 0
      trackingPixel.height = 0
      trackingPixel.id = 'signup-tracking-pixel'
      document.head.appendChild(trackingPixel)

      return () => {
        const pixel = document.getElementById('signup-tracking-pixel')
        if (pixel)
          document.head.removeChild(pixel)
      }
    }
  }, [isLogin])

  useEffect(() => {
    // Verify Default SDK is loaded
    if (window.DefaultSDK) {
      window.DefaultSDK.helloWorld() // This will log "Hello World! Welcome to Default.com"
    }
    else {
      console.warn('Default SDK not loaded yet')
    }
  }, [])

  useEffect(() => {
    const completeSSOAuthentication = async () => {
      const encodedValue = encodeURIComponent(browserSessionToken)
      await handleAuthResponse({ jwt: encodedValue })
      deleteBrowserSessionToken()
    }

    if (isSSOCallback) {
      if (!user && browserSessionToken) {
        setLoading(true)
        completeSSOAuthentication()
      }
      else if (user?.user?.phone_number) {
        navigate('/dashboard')
      }
      else if (user) {
        setLoading(false)
      }
    }
  }, [isSSOCallback, browserSessionToken, user])

  return (
    <div className="relative h-screen w-screen overflow-x-hidden">
      <img alt="logo" src={logo} className="absolute top-6 left-9 z-50 h-9" />
      {currentStep === 4
        ? (
            <motion.div
              className={`
                relative flex h-full w-full items-center justify-center
              `}
              initial={{ backgroundColor: '#FFFFFF' }}
              animate={{ backgroundColor: '#F1EFE8' }}
              transition={{ duration: 2.5 }}
            >
              {isLogin
                ? (
                    <AccountSelect />
                  )
                : (
                    <WebDemo />
                  )}
            </motion.div>
          )
        : (
            <div className={`
              grid h-screen w-full grid-cols-1
              xl:grid-cols-2
            `}
            >
              <Form
                onSubmit={handleSubmit(onSubmit)}
                className={cn(`
                  flex h-screen w-full max-w-(--breakpoint-sm) flex-col
                  justify-center gap-5 px-8
                  xl:items-start xl:px-20
                `, currentStep > 2
                  ? `place-self-center`
                  : `
                    place-self-center
                    xl:place-self-start
                  `)}
              >
                {currentStep === 1 && (
                  <>
                    <div className="flex w-full items-end justify-between">
                      <h1 className={`
                        w-full font-taurus text-5xl font-semibold
                        tracking-tighter
                      `}
                      >
                        {isSSOCallback ? 'Add your number' : isSSOLogin ? 'Log in with SSO' : isLogin ? 'Log in to Bland' : 'Sign up with Bland'}
                      </h1>

                      <RegionSelector />
                    </div>

                    {!isLogin
                      ? (
                          <>
                            <div className="flex w-full justify-between gap-4">
                              <Input
                                radius="sm"
                                variant="bordered"
                                label="First Name"
                                isRequired
                                {...register('first_name')}
                              />
                              <Input
                                radius="sm"
                                variant="bordered"
                                label="Last Name"
                                isRequired
                                {...register('last_name')}
                              />
                            </div>
                            <Controller
                              name="phone"
                              control={control}
                              render={({ field }) => (
                                <PhoneInput
                                  isRequired
                                  fullWidth
                                  size="lg"
                                  {...field}
                                />
                              )}
                            />
                            <Input
                              radius="sm"
                              variant="bordered"
                              label="Email"
                              type="email"
                              isRequired
                              {...register('email')}
                            />
                            <span className="flex flex-col gap-0.5">
                              <div className="flex items-baseline">
                                <Checkbox
                                  size="sm"
                                  radius="none"
                                  {...register('agreed_tos')}
                                  classNames={{ base: 'mt-0.5' }}
                                  isRequired
                                />
                                <span className="text-xs text-gray-500">
                                  I agree to the
                                  {' '}
                                  <a
                                    href="https://www.bland.ai/legal/terms-of-service"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline"
                                    onClick={e => e.stopPropagation()}
                                  >
                                    terms of service
                                  </a>
                                  *
                                </span>
                              </div>

                              <Checkbox
                                size="sm"
                                radius="none"
                                {...register('marketing_consent')}
                                classNames={{ label: 'text-xs mt-2.5 text-gray-400' }}
                              >
                                I agree to receive occasional emails and SMS messages about new features and updates from Bland AI. I can unsubscribe at any time.
                              </Checkbox>
                            </span>
                          </>
                        )
                      : isSSOLogin
                        ? (
                            <Input
                              radius="sm"
                              variant="bordered"
                              label="Organization Email"
                              type="email"
                              {...register('email')}
                            />
                          )
                        : (
                            <Controller
                              name="phone"
                              control={control}
                              render={({ field }) => (
                                <PhoneInput
                                  isRequired
                                  fullWidth
                                  size="lg"
                                  {...field}
                                />
                              )}
                            />
                          )}

                    <AuthButton type="submit" isDisabled={turnstileError} isLoading={isLoading}>
                      {isSSOLogin ? 'Sign in' : isLogin ? 'Get Code' : 'Sign Up'}
                    </AuthButton>

                    {!isSSOCallback && !isSSOLogin && (
                      <>
                        {/* SSO Login Option */}
                        {isLogin && (
                          <div className="flex w-full flex-col gap-3">
                            <div className="flex items-center gap-3">
                              <div className="h-px flex-1 bg-gray-300"></div>
                              <span className="text-sm text-gray-500">or</span>
                              <div className="h-px flex-1 bg-gray-300"></div>
                            </div>

                            <Button
                              type="button"
                              variant="bordered"
                              radius="none"
                              onPress={() => {
                                setIsSSOLogin(true)
                              }}
                              disabled={isLoading}
                            >
                              <Key className="size-4" />
                              Sign in with SSO
                            </Button>
                          </div>
                        )}

                        <div className="flex flex-row gap-0.5">
                          <p className={`
                            font-taurus font-medium text-[#151515]/50
                          `}
                          >
                            {isLogin
                              ? 'Need an account?'
                              : 'Already have an account?'}
                          </p>
                          <p
                            onClick={() => {
                              isLogin ? navigate('/signup') : navigate('/login')
                            }}
                            className={`
                              font-bold underline decoration-solid
                              hover:cursor-pointer
                            `}
                          >
                            {isLogin ? 'Sign up here' : 'Log in here'}
                          </p>
                        </div>
                      </>
                    )}

                    <Turnstile
                      siteKey={TURNSTILE_SITE_KEY}
                      className="mx-auto"
                      onSuccess={handleTurnstileSuccess}
                      onError={handleTurnstileError}
                      options={{
                        appearance: 'interaction-only',
                        theme: 'light',
                        size: 'flexible',
                        action: 'auth',
                      }}
                    />
                  </>
                )}

                {currentStep === 2 && (
                  <span className="mx-auto flex w-fit flex-col items-start">
                    <p className="text-center text-sm text-gray-500">Enter the code sent to your phone</p>
                    <InputOtp
                      variant="faded"
                      {...register('sms_code')}
                      size="lg"
                      autoFocus
                      length={6}
                      classNames={{ errorMessage: 'w-80 text-wrap' }}
                    />
                    <AuthButton
                      type="submit"
                      isLoading={isLoading}
                      className="mt-4"
                    >
                      Verify Code
                    </AuthButton>
                  </span>
                )}

                {currentStep === 3 && (
                  <span className="mx-auto flex w-fit flex-col items-start">
                    <p className="text-center text-sm text-gray-500">Enter the code sent to your email</p>
                    <InputOtp
                      variant="faded"
                      autoFocus
                      {...register('email_code')}
                      size="lg"
                      length={6}
                    />
                    <AuthButton
                      type="submit"
                      isLoading={isLoading}
                      className="mt-4"
                    >
                      Verify Code
                    </AuthButton>
                  </span>
                )}
              </Form>

              <Promotion type={isLogin ? 'login' : 'signup'} />
            </div>
          )}
    </div>
  )
}
